export function getDefaultShoppingListName(date = new Date()) {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `Compras ${day}/${month}/${year}`;
}

/** Setores fixos de supermercado (exibidos no combobox de cada item). */
export const isShoppingItemFilled = (item) =>
    !!(item?.catalogItem?.name && item.catalogItem.name.trim() !== '');

export const getShoppingItemKey = (item) =>
    item?.id ?? item?._clientKey ?? '';

let clientKeySeq = 0;
export const nextShoppingItemClientKey = () => {
    clientKeySeq += 1;
    return `shopping-item-${clientKeySeq}`;
};

/** Chave de ordenação por setor: usa o setor "congelado" (_sortSector) quando presente. */
const sectorSortKey = (item) =>
    (item && item._sortSector !== undefined ? item._sortSector : (item?.supermarketSector || '')) || '';

function compareByColumn(column, direction, a, b) {
    const mult = direction === 'desc' ? -1 : 1;
    let cmp = 0;
    switch (column) {
    case 'purchased':
        cmp = (a.purchased ? 1 : 0) - (b.purchased ? 1 : 0);
        break;
    case 'item':
        cmp = (a.catalogItem?.name || '').localeCompare(
            b.catalogItem?.name || '',
            'pt-BR',
            {sensitivity: 'base'},
        );
        break;
    case 'sector':
        // _sortSector congela a posição de ordenação enquanto a troca de setor está
        // "efetivando" (fade), para que o efeito ocorra antes da reordenação.
        cmp = sectorSortKey(a).localeCompare(
            sectorSortKey(b),
            'pt-BR',
            {sensitivity: 'base'},
        );
        break;
    case 'quantity':
        cmp = (parseInt(a.quantity, 10) || 0) - (parseInt(b.quantity, 10) || 0);
        break;
    default:
        cmp = (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
        break;
    }
    return cmp * mult;
}

/**
 * Ordena itens preenchidos. sortConfig pode ser:
 *   - legado: {column, direction}
 *   - cascateado: {primary: {column, direction}, secondary: {column, direction} | null}
 */
export function sortShoppingListItems(items, sortConfig = {}) {
    let primary, secondary;
    if ('primary' in sortConfig) {
        primary = sortConfig.primary;
        secondary = sortConfig.secondary || null;
    } else {
        primary = {column: sortConfig.column ?? null, direction: sortConfig.direction ?? 'asc'};
        secondary = null;
    }

    const emptyRows = (items || []).filter((item) => !isShoppingItemFilled(item));
    const filled = (items || []).filter((item) => isShoppingItemFilled(item));

    filled.sort((a, b) => {
        if (primary.column !== 'purchased') {
            const aPurchased = !!a.purchased;
            const bPurchased = !!b.purchased;
            if (aPurchased !== bPurchased) {
                return aPurchased ? 1 : -1;
            }
        }

        const primaryCmp = compareByColumn(primary.column, primary.direction, a, b);
        if (primaryCmp !== 0 || !secondary) return primaryCmp;
        return compareByColumn(secondary.column, secondary.direction, a, b);
    });

    return [...filled, ...emptyRows];
}

export const SUPERMARKET_SECTORS = [
    'Hortifruti',
    'Açougue',
    'Peixaria',
    'Frios e laticínios',
    'Padaria',
    'Mercearia',
    'Bebidas',
    'Limpeza',
    'Higiene e beleza',
    'Congelados',
    'Bazar',
    'Pet shop',
    'Outros',
];

/** Cores predefinidas para os setores padrão (diferencia cada setor por cor). */
export const SUPERMARKET_SECTOR_COLORS = {
    'Hortifruti': '#7cb342',
    'Açougue': '#e53935',
    'Peixaria': '#26c6da',
    'Frios e laticínios': '#fff176',
    'Padaria': '#d7a86e',
    'Mercearia': '#ffb74d',
    'Bebidas': '#5c6bc0',
    'Limpeza': '#4dd0e1',
    'Higiene e beleza': '#f48fb1',
    'Congelados': '#90caf9',
    'Bazar': '#a1887f',
    'Pet shop': '#ba68c8',
    'Outros': '#b0bec5',
};

const DEFAULT_SECTOR_COLOR = '#e0e0e0';

/** Normaliza um nome de setor para comparação (case-insensitive, sem espaços nas pontas). */
const normalizeSectorName = (name) => (name || '').trim().toLowerCase();

/**
 * Resolve a cor de um setor pelo nome: cor do setor custom (mapa nome→cor) tem prioridade;
 * senão a cor do setor padrão; senão uma cor neutra.
 */
export const resolveSectorColor = (name, customColorMap) => {
    const key = normalizeSectorName(name);
    if (!key) return DEFAULT_SECTOR_COLOR;
    if (customColorMap && customColorMap[key]) return customColorMap[key];
    const defaultEntry = Object.keys(SUPERMARKET_SECTOR_COLORS)
        .find((s) => normalizeSectorName(s) === key);
    return defaultEntry ? SUPERMARKET_SECTOR_COLORS[defaultEntry] : DEFAULT_SECTOR_COLOR;
};

/** Cor de texto (escuro/claro) legível sobre a cor de fundo informada (hex #rgb ou #rrggbb). */
export const sectorTextColor = (hex) => {
    const h = (hex || '').replace('#', '').trim();
    let r;
    let g;
    let b;
    if (h.length === 3) {
        r = parseInt(h[0] + h[0], 16);
        g = parseInt(h[1] + h[1], 16);
        b = parseInt(h[2] + h[2], 16);
    } else if (h.length === 6) {
        r = parseInt(h.slice(0, 2), 16);
        g = parseInt(h.slice(2, 4), 16);
        b = parseInt(h.slice(4, 6), 16);
    } else {
        return '#222';
    }
    // Luminância relativa simplificada
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.6 ? '#222' : '#fff';
};
